import * as React from 'react'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import {
  KeyboardTypeOptions,
  NativeSyntheticEvent,
  Platform,
  ReturnKeyTypeOptions,
  TextInput as RNTextInput,
  TextInputKeyPressEventData,
  TextInputSubmitEditingEventData,
} from 'react-native'
import styled, { ThemeContext } from 'styled-components/native'
import { color, flexbox, FlexboxProps, layout, space, typography } from 'styled-system'
import { ColorOptions, FontSizeOptions, FontWeightOptions } from '../../theme/UiThemeProvider'
import { useBreakPointProps } from '../../utils/utils'
import { Box, BoxProps } from '../Box'
import { Spacer } from '../Spacer'
import { Text } from '../Text'
import styles from '../Text/styles'

const StyledTextInput = styled.TextInput<FlexboxProps>`
  ${color}
  ${layout}
  ${typography}
  ${flexbox}
  ${space}
`

type FontWeightValues = 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900'
export interface TextInputProps {
  type?: TypesOption
  isStartSelection?: Boolean
  innerRef?: React.RefObject<RNTextInput>
  fontSize?: FontSizeOptions | number | Array<FontSizeOptions | number>
  fontWeight?: FontWeightOptions | FontWeightValues | Array<FontWeightOptions | FontWeightValues>
  color?: ColorOptions | Array<ColorOptions>
  selectionColor?: ColorOptions | Array<ColorOptions>
  label?: string
  value?: string
  onChange?: (text: string) => void
  onChangeText?: (text: string) => void
  onTouchEnd?: () => void
  useText?: boolean
  placeholder?: string
  placeholderTextColor?: ColorOptions | Array<ColorOptions>
  showContainerPadding?: boolean
  showBottomLine?: boolean
  fontFamily?: any
  bottomLineColor?: ColorOptions | Array<ColorOptions>
  onFocusBottomLineColor?: ColorOptions | Array<ColorOptions>
  isSecret?: boolean
  maxLength?: number | Array<number>
  leftComponent?: React.ReactNode
  rightComponent?: React.ReactNode
  keyboardType?: KeyboardTypeOptions
  editable?: boolean
  multiline?: boolean
  numberOfLines?: number
  cursorPosition?: number
  onBlur?: () => void
  onFocus?: () => void
  onSelectionChange?: (event: any) => void
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void
  secureTextEntry?: boolean
  autoCompleteType?:
    | 'cc-csc'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-number'
    | 'email'
    | 'name'
    | 'password'
    | 'postal-code'
    | 'street-address'
    | 'tel'
    | 'username'
    | 'off'
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters'
  autoCorrect?: boolean
  onPressIn?: () => void
  onPressOut?: () => void
  onPress?: () => void
  error?: string
  touched?: boolean
  autoFocus?: boolean
  weight?: 'normal' | 'dense' | Array<'normal' | 'dense'>
  textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify' | Array<'auto' | 'left' | 'right' | 'center' | 'justify'>
  textWidth?: number | Array<number>
  hide?: boolean
  isAutoFocus?: boolean
  returnKeyType?: ReturnKeyTypeOptions
  onSubmitEditing?: (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => void
  testID?: string
  accessibilityLabel?: string
  nativeID?: string
  caretHidden?: boolean
  minHeight?: number | undefined
  lineHeight?: number | undefined
  textContentType?:
    | 'none'
    | 'URL'
    | 'addressCity'
    | 'addressCityAndState'
    | 'addressState'
    | 'countryName'
    | 'creditCardNumber'
    | 'emailAddress'
    | 'familyName'
    | 'fullStreetAddress'
    | 'givenName'
    | 'jobTitle'
    | 'location'
    | 'middleName'
    | 'name'
    | 'namePrefix'
    | 'nameSuffix'
    | 'nickname'
    | 'organizationName'
    | 'postalCode'
    | 'streetAddressLine1'
    | 'streetAddressLine2'
    | 'sublocality'
    | 'telephoneNumber'
    | 'username'
    | 'password'
    | 'newPassword'
    | 'oneTimeCode'
    | undefined
  errorTextVerticalSpacing?: number
  errorTextAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify'
  errorTextFontWeight?: FontWeightOptions | FontWeightValues | Array<FontWeightOptions | FontWeightValues>
  lableFontWeight?: FontWeightOptions | FontWeightValues | Array<FontWeightOptions | FontWeightValues>
  inputMode?: string
}

type TypesOption = keyof typeof styles
export const TextTypesOptionArray = Object.keys(styles)

const removeOutline =
  Platform.OS === 'web'
    ? {
        outline: 'none',
      }
    : { color: 'black' }

export const TextInput: FC<TextInputProps> = (props) => {
  const modifiedProps = useBreakPointProps<TextInputProps>(props)
  const {
    type,
    onChange: onChangeText,
    label,
    innerRef,
    showContainerPadding = true,
    showBottomLine = true,
    isStartSelection = false,
    bottomLineColor = 'LIGHT_GREY_INTRO',
    leftComponent = null,
    rightComponent = null,
    onPressIn,
    onPressOut,
    touched,
    error,
    weight = 'normal',
    textWidth,
    hide = false,
    useText = false,
    onFocus,
    onBlur,
    onFocusBottomLineColor = 'PRIMARY',
    placeholderTextColor: propPlaceholderTextColor,
    onSelectionChange,
    testID,
    isAutoFocus = false,
    selectionColor: propSelectionColor,
    cursorPosition,
    nativeID,
    caretHidden = false,
    minHeight = undefined,
    lineHeight = undefined,
    textContentType,
    errorTextVerticalSpacing = 2,
    errorTextAlign = 'left',
    errorTextFontWeight = '500',
    fontWeight = '700',
    lableFontWeight = '700',
    onPress: externalOnPress,
    ...restProps
  } = modifiedProps
  const textInputRef = useRef<RNTextInput>(null)
  const [isFocused, setFocused] = useState(false)
  const [isPressed, setPressed] = useState(false)
  const [showError, setShowError] = useState(false)
  const errorTimerRef = useRef<any>(0)
  const theme = useContext(ThemeContext)
  let selectionColor = propSelectionColor
  if (selectionColor && theme?.colors[selectionColor as ColorOptions]) {
    selectionColor = theme?.colors[selectionColor as ColorOptions] ?? selectionColor
  }
  let placeholderTextColor = propPlaceholderTextColor
  if (placeholderTextColor && theme?.colors[placeholderTextColor as ColorOptions]) {
    placeholderTextColor = theme?.colors[placeholderTextColor as ColorOptions] ?? placeholderTextColor
  }
  const isError = !isFocused && touched && !!error && !isPressed
  useEffect(() => {
    if (isError) {
      errorTimerRef.current = setTimeout(() => {
        setShowError(true)
      }, 500)
    } else {
      clearTimeout(errorTimerRef.current)
      setShowError(false)
    }
  }, [isError])
  if (hide) {
    return null
  }

  const refUsed = innerRef ?? textInputRef
  const modifiedBottomLineColor = isFocused ? onFocusBottomLineColor : bottomLineColor
  const textInputContainerStyle: BoxProps =
    !showContainerPadding && showBottomLine ? {} : showBottomLine ? { paddingVertical: 8 } : {}
  const onPress = () => {
    if (refUsed.current) {
      refUsed.current.focus()
    }
    if (externalOnPress) {
      externalOnPress()
    }
  }
  const onInternalFocus = () => {
    setFocused(true)
    if (onFocus) {
      onFocus()
    }
  }
  const onInternalBlur = () => {
    setFocused(false)
    if (onBlur) {
      onBlur()
    }
  }
  const typeStyle = type ? styles[type][weight as 'normal' | 'dense'] : {}
  const Component: any = useText && !restProps.editable ? Text : StyledTextInput
  return (
    <Box width="100%">
      {label && (
        <Box>
          <Text
            fontWeight={lableFontWeight}
            color={isFocused ? onFocusBottomLineColor : isAutoFocus ? onFocusBottomLineColor : 'LIGHT_BLACK'}
            type="h4"
            title={label}
          />
          <Spacer size={2} />
        </Box>
      )}
      <Box width="100%">
        <Box
          alignSelf="stretch"
          alignItems="center"
          flexDirection="row"
          underlayColor="TRANSPARENT"
          onPress={onPress}
          onPressIn={() => {
            setPressed(true)
            if (onPressIn) {
              onPressIn()
            }
          }}
          onPressOut={() => {
            setPressed(false)
            if (onPressOut) {
              onPressOut()
            }
          }}
          overflow="hidden"
          {...textInputContainerStyle}>
          {leftComponent}
          {leftComponent && <Spacer direction="horizontal" size={8} />}
          <Box flex={1}>
            <Component
              nativeID={nativeID}
              padding={0}
              width={textWidth}
              ref={refUsed}
              onChangeText={onChangeText}
              onPressIn={onPressIn}
              onPressOut={onPressOut}
              onFocus={onInternalFocus}
              onBlur={onInternalBlur}
              onSelectionChange={onSelectionChange}
              selection={
                isStartSelection
                  ? { start: 0, end: 0 }
                  : cursorPosition !== -1 && cursorPosition !== undefined
                  ? { start: cursorPosition, end: cursorPosition }
                  : null
              }
              fontWeight={fontWeight}
              fontSize={16}
              testID={testID}
              selectionColor={selectionColor}
              placeholderTextColor={placeholderTextColor}
              caretHidden={caretHidden}
              minHeight={minHeight}
              lineHeight={lineHeight}
              textContentType={textContentType}
              {...typeStyle}
              {...restProps}
              style={removeOutline}
            />
          </Box>
          {rightComponent && <Spacer direction="horizontal" size={8} />}
          {rightComponent}
        </Box>
        {showBottomLine && (
          <Box
            height={1}
            alignSelf="stretch"
            backgroundColor={!showError ? modifiedBottomLineColor : 'ERROR'}
            opacity={isFocused ? 1 : 0.6}
          />
        )}
      </Box>

      {showError && (
        <>
          <Spacer size={errorTextVerticalSpacing} />
          <Text
            testID={'textInputErrorId'}
            color="ERROR"
            type="h4"
            textAlign={errorTextAlign}
            fontWeight={errorTextFontWeight}>
            {error}
          </Text>
        </>
      )}
    </Box>
  )
}
